import React, { useEffect, useState } from 'react';
import { Platform, Text, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { BREAKPOINTS } from '../styles/breakpoints';
import { CANVA_NAV_BAR, CANVA_NAV_TITLE } from '../utils/hamburgerMenu';

export const REVIEW_VRC_LABEL = 'Review VRC';
export const REVIEW_VRC_PROMPT = "Let us know how everything is working and what you'd like to see";
export const REVIEW_VRC_TOPIC = 'review';

/**
 * Routes that hide the button: the Contact form it opens, the full-screen maps (Map and a
 * shared tour), whose lower-right corner already holds map controls, and the three card
 * editors, whose grids run to the lower-right corner on phones.
 */
export const REVIEW_VRC_HIDDEN_ROUTES = [
  'Contact',
  'TourPlanner',
  'SharedTourView',
  'CommunityPostCreate',
  'GearSwapPostCreate',
  'ProfileCardEdit',
];

/**
 * Extra bottom space the in-flow phone footer reserves so the fixed 44px button (12px off
 * the bottom edge) never covers its last links: 12 + 44 + 12 gap.
 */
export const REVIEW_VRC_MOBILE_CLEARANCE = 68;

export function shouldShowReviewVrcButton(routeName) {
  if (!routeName) return false;
  return !REVIEW_VRC_HIDDEN_ROUTES.includes(routeName);
}

/**
 * REQ-070: sitewide lower-right "Review VRC" chat button. It opens the existing Contact
 * form (contact_submissions + the contact_form email) preset to the Review VRC topic, so
 * there is no new backend. Web only; no motion, so nothing to turn off for reduced motion.
 */
export function ReviewVrcButton({ rightInset = 0, bottomInset = 0 }) {
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const [routeName, setRouteName] = useState(null);

  useEffect(() => {
    if (!navigation) return undefined;
    const update = () => {
      try {
        if (navigation.isReady && !navigation.isReady()) return;
        setRouteName(navigation.getCurrentRoute?.()?.name || null);
      } catch (_) {
        // Root ref not ready yet; the next state event fills it in.
      }
    };
    update();
    const unsubscribe = navigation.addListener?.('state', update);
    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }, [navigation]);

  if (Platform.OS !== 'web' || !shouldShowReviewVrcButton(routeName)) return null;

  const isPhone = width <= BREAKPOINTS.mobile;
  const edge = isPhone ? 12 : 20;

  return (
    <TouchableOpacity
      onPress={() => navigation.navigate('Contact', { topic: REVIEW_VRC_TOPIC })}
      accessibilityRole="button"
      accessibilityLabel={`${REVIEW_VRC_LABEL}: ${REVIEW_VRC_PROMPT}`}
      activeOpacity={0.85}
      style={{
        position: 'fixed',
        right: edge + rightInset,
        bottom: edge + bottomInset,
        zIndex: 900,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        minHeight: 44,
        paddingHorizontal: isPhone ? 14 : 18,
        borderRadius: 22,
        backgroundColor: CANVA_NAV_BAR,
        borderWidth: 1,
        borderColor: 'rgba(226,222,222,0.28)',
        boxShadow: '0 6px 18px rgba(0,0,0,0.28)',
        cursor: 'pointer',
      }}
    >
      <Ionicons name="chatbubble-ellipses-outline" size={isPhone ? 18 : 20} color={CANVA_NAV_TITLE} />
      <Text
        style={{
          color: CANVA_NAV_TITLE,
          fontFamily: 'VastagoGrotesk',
          fontSize: isPhone ? 13 : 14,
          fontWeight: '700',
          letterSpacing: 0.3,
        }}
      >
        {REVIEW_VRC_LABEL}
      </Text>
    </TouchableOpacity>
  );
}

export default ReviewVrcButton;
